import { useEffect, useState } from 'react';
import authService, { type SignInProvider } from '../../services/auth.service';
import { colors } from '../../styles/theme';
import { signInStartFailureCopy } from './signIn.helpers';

function GoogleMark(): JSX.Element {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function MicrosoftMark(): JSX.Element {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
      <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

function AppleMark(): JSX.Element {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#000000" aria-hidden="true" focusable="false">
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
    </svg>
  );
}

/** The mark that makes a known option recognisable at a glance. An option
 *  without one gets a text-only button. */
function ProviderMark({ providerId }: { readonly providerId: string }): JSX.Element | null {
  switch (providerId) {
    case 'google':
      return <GoogleMark />;
    case 'microsoft':
      return <MicrosoftMark />;
    case 'apple':
      return <AppleMark />;
    default:
      return null;
  }
}

interface SignInProviderButtonsProps {
  /** The options to offer, in display order. */
  readonly providers: readonly SignInProvider[];
  /** Sent along when the sign-in is started from an invitation link, so the
   *  invitation is redeemed as part of it. */
  readonly invitationToken?: string;
  /** Lets the page hold the buttons while its own form is busy. */
  readonly disabled?: boolean;
}

/**
 * One "Continue with {name}" button per sign-in option, shared by the login
 * page and the invitation page. Choosing one asks the backend where to send the
 * browser and goes there; the person comes back through /oauth/callback.
 * Renders nothing when there are no options.
 */
export default function SignInProviderButtons({
  providers,
  invitationToken,
  disabled = false,
}: SignInProviderButtonsProps): JSX.Element | null {
  // The option whose start is in flight. Every button is held until it
  // settles, so a second click cannot start a second sign-in.
  const [startingId, setStartingId] = useState<string | null>(null);
  const [error, setError] = useState('');

  // The buttons stay held while the browser leaves for the chosen account's
  // page. Coming back with the Back button can restore this page exactly as it
  // was left, and it must be usable again.
  useEffect(() => {
    const release = (event: PageTransitionEvent): void => {
      if (event.persisted) setStartingId(null);
    };
    globalThis.addEventListener('pageshow', release);
    return () => globalThis.removeEventListener('pageshow', release);
  }, []);

  if (providers.length === 0) return null;

  const start = async (providerId: string): Promise<void> => {
    setError('');
    setStartingId(providerId);
    try {
      globalThis.location.href = await authService.startProviderSignIn(
        providerId,
        invitationToken,
      );
    } catch (err: unknown) {
      setError(signInStartFailureCopy(err));
      setStartingId(null);
    }
  };

  const held = disabled || startingId !== null;

  return (
    <div>
      <div className="space-y-3">
        {providers.map((provider) => (
          <button
            key={provider.id}
            type="button"
            onClick={() => { start(provider.id); }}
            disabled={held}
            aria-busy={startingId === provider.id}
            className="w-full px-4 py-2.5 rounded-xl flex items-center justify-center gap-3 text-sm font-medium transition-all border-[1.5px] border-[#E5E7EB] bg-[#FAFAFA] text-[#374151] enabled:hover:border-[#D1D5DB] focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ outlineColor: colors.gold }}
          >
            <ProviderMark providerId={provider.id} />
            Continue with {provider.name}
          </button>
        ))}
      </div>
      {error && (
        <p
          role="alert"
          className="mt-3 p-3 rounded-lg text-sm"
          style={{
            background: colors.dangerBg,
            border: `1px solid ${colors.dangerBorder}`,
            color: colors.danger,
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
}
